// src/stores/assistantStore.ts
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { ChatMessage } from "@/services/assistant/AssistantService";
import type { AgentId, MultiAgentStreamState, ToolState, ChartSpec, UIGroup } from "@/types/assistant";
import { createInitialAgentState, createInitialMultiAgentState } from "@/types/assistant";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ConversationStreamState {
  messages: ChatMessage[];
  planningByMessage: Record<string, boolean>;
  toolEventsByMessage: Record<string, unknown[]>;
  isStreaming: boolean;
  multiAgent?: MultiAgentStreamState | undefined;
  /** Snapshots of multiAgent state per message ID (for chart rendering in historical messages) */
  multiAgentByMessageId: Record<string, MultiAgentStreamState>;
}

interface AssistantStoreState {
  // ─── Global UI State ───
  activeConversationId: string | undefined;
  preserveOnUnmount: boolean;

  // ─── Conversation States ───
  conversations: Record<string, ConversationStreamState>;

  // ─── Abort Controllers (non-serializable, kept in store for convenience) ───
  controllers: Map<string, AbortController>;
}

interface AssistantStoreActions {
  // ─── Global UI Actions ───
  setActiveConversationId: (id: string | undefined) => void;
  setPreserveOnUnmount: (preserve: boolean) => void;

  // ─── Conversation State Actions ───
  getConversation: (conversationId: string) => ConversationStreamState | undefined;
  getOrCreateConversation: (conversationId: string) => ConversationStreamState;
  setBaseline: (conversationId: string, messages: ChatMessage[]) => void;
  clearConversation: (conversationId: string) => void;

  // ─── Streaming Lifecycle ───
  initStream: (conversationId: string, messages: ChatMessage[], streamId: string) => void;
  setStreaming: (conversationId: string, streaming: boolean) => void;
  stopStream: (conversationId: string) => void;
  getController: (conversationId: string) => AbortController | undefined;
  setController: (conversationId: string, controller: AbortController) => void;
  removeController: (conversationId: string) => void;

  // ─── Multi-Agent State Updates ───
  setPlannedAgents: (conversationId: string, agents: AgentId[]) => void;
  setAgentStart: (conversationId: string, agentId: AgentId, displayName: string, uiGroup: UIGroup, streamId: string) => void;
  setAgentDraft: (conversationId: string, agentId: AgentId, text: string) => void;
  setAgentDone: (conversationId: string, agentId: AgentId, ok: boolean, irrelevant: boolean, error?: string) => void;
  appendToken: (conversationId: string, delta: string, agentId: AgentId, channel: "draft" | "final", streamId: string) => void;
  appendThinking: (conversationId: string, agentId: AgentId, delta?: string, text?: string) => void;
  addToolCalling: (conversationId: string, agentId: AgentId, streamId: string, tool: ToolState) => void;
  completeToolCall: (conversationId: string, agentId: AgentId, streamId: string, tool: ToolState) => void;
  addCharts: (conversationId: string, charts: ChartSpec[]) => void;
  setFinalMessage: (conversationId: string, text: string, streamId: string) => void;
  setPlanningComplete: (conversationId: string, streamId: string) => void;

  // ─── Multi-Agent Snapshots ───
  setMultiAgentSnapshot: (conversationId: string, messageId: string, snapshot: MultiAgentStreamState) => void;
  setMultiAgentSnapshots: (conversationId: string, snapshots: Record<string, MultiAgentStreamState>) => void;

  // ─── Error Handling ───
  handleStreamError: (conversationId: string, streamId: string) => void;
}

type AssistantStore = AssistantStoreState & AssistantStoreActions;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function createEmptyConversation(): ConversationStreamState {
  return {
    messages: [],
    planningByMessage: {},
    toolEventsByMessage: {},
    isStreaming: false,
    multiAgentByMessageId: {},
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useAssistantStore = create<AssistantStore>()(
  devtools(
    (set, get) => ({
      // ─── Initial State ───
      activeConversationId: undefined,
      preserveOnUnmount: false,
      conversations: {},
      controllers: new Map(),

      // ─── Global UI Actions ───
      setActiveConversationId: (id) => set({ activeConversationId: id }),
      setPreserveOnUnmount: (preserve) => set({ preserveOnUnmount: preserve }),

      // ─── Conversation State Actions ───
      getConversation: (conversationId) => get().conversations[conversationId],

      getOrCreateConversation: (conversationId) => {
        const existing = get().conversations[conversationId];
        if (existing) return existing;
        const newConv = createEmptyConversation();
        set((state) => ({
          conversations: { ...state.conversations, [conversationId]: newConv },
        }));
        return newConv;
      },

      setBaseline: (conversationId, messages) =>
        set((state) => {
          // Don't overwrite if streaming is active - prevents hydration from killing active streams
          const existing = state.conversations[conversationId];
          if (existing?.isStreaming) {
            return state;
          }
          
          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...(existing ?? createEmptyConversation()),
                messages,
                planningByMessage: {},
                toolEventsByMessage: {},
                isStreaming: false,
                // Preserve multiAgentByMessageId for historical charts
                multiAgentByMessageId: existing?.multiAgentByMessageId ?? {},
              },
            },
          };
        }),

      clearConversation: (conversationId) =>
        set((state) => {
          const { [conversationId]: _removed, ...rest } = state.conversations;
          void _removed; // Intentionally discarded
          return { conversations: rest };
        }),

      // ─── Streaming Lifecycle ───
      initStream: (conversationId, messages, streamId) =>
        set((state) => {
          const conv = state.conversations[conversationId] ?? createEmptyConversation();
          const multiAgent = createInitialMultiAgentState();
          multiAgent.active = true;
          multiAgent.conversationId = conversationId;

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                messages: [...messages, { id: streamId, role: "assistant", content: "" }],
                planningByMessage: { ...conv.planningByMessage, [streamId]: true },
                toolEventsByMessage: { ...conv.toolEventsByMessage, [streamId]: [] },
                isStreaming: true,
                multiAgent,
              },
            },
          };
        }),

      setStreaming: (conversationId, streaming) =>
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv) return state;
          return {
            conversations: {
              ...state.conversations,
              [conversationId]: { ...conv, isStreaming: streaming },
            },
          };
        }),

      stopStream: (conversationId) => {
        const controller = get().controllers.get(conversationId);
        if (controller) {
          controller.abort();
          get().controllers.delete(conversationId);
        }

        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv) return state;

          const updatedPlanning = { ...conv.planningByMessage };
          for (const key of Object.keys(updatedPlanning)) {
            updatedPlanning[key] = false;
          }

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                isStreaming: false,
                planningByMessage: updatedPlanning,
                multiAgent: conv.multiAgent
                  ? { ...conv.multiAgent, active: false }
                  : undefined,
              },
            },
          };
        });
      },

      getController: (conversationId) => get().controllers.get(conversationId),
      setController: (conversationId, controller) => {
        get().controllers.set(conversationId, controller);
      },
      removeController: (conversationId) => {
        get().controllers.delete(conversationId);
      },

      // ─── Multi-Agent State Updates ───
      setPlannedAgents: (conversationId, agents) =>
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv?.multiAgent) return state;

          const updatedAgents = { ...conv.multiAgent.agents };
          for (const agentId of agents) {
            if (!updatedAgents[agentId]) {
              updatedAgents[agentId] = createInitialAgentState(agentId);
            }
          }

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                multiAgent: {
                  ...conv.multiAgent,
                  plannedAgents: agents,
                  agents: updatedAgents,
                },
              },
            },
          };
        }),

      setAgentStart: (conversationId, agentId, displayName, uiGroup, streamId) =>
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv?.multiAgent) return state;

          const agent = conv.multiAgent.agents[agentId] ?? createInitialAgentState(agentId);
          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                planningByMessage: { ...conv.planningByMessage, [streamId]: false },
                multiAgent: {
                  ...conv.multiAgent,
                  agents: {
                    ...conv.multiAgent.agents,
                    [agentId]: { ...agent, status: "working", displayName, uiGroup },
                  },
                },
              },
            },
          };
        }),

      setAgentDraft: (conversationId, agentId, text) =>
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv?.multiAgent) return state;

          const agent = conv.multiAgent.agents[agentId];
          if (!agent) return state;

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                multiAgent: {
                  ...conv.multiAgent,
                  agents: {
                    ...conv.multiAgent.agents,
                    [agentId]: { ...agent, draftText: text },
                  },
                },
              },
            },
          };
        }),

      setAgentDone: (conversationId, agentId, ok, irrelevant, error) =>
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv?.multiAgent) return state;

          const agent = conv.multiAgent.agents[agentId];
          if (!agent) return state;

          const completedTools = [...agent.completedTools];
          agent.activeTools.forEach((tool) => {
            completedTools.push({ ...tool, phase: "completed" });
          });

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                multiAgent: {
                  ...conv.multiAgent,
                  agents: {
                    ...conv.multiAgent.agents,
                    [agentId]: {
                      ...agent,
                      status: irrelevant ? "irrelevant" : !ok || error ? "error" : "done",
                      error,
                      activeTools: new Map(),
                      completedTools,
                    },
                  },
                },
              },
            },
          };
        }),

      appendToken: (conversationId, delta, agentId, channel, streamId) =>
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv?.multiAgent) return state;

          if (channel === "final") {
            const newFinalOutput = conv.multiAgent.finalOutput + delta;
            return {
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...conv,
                  messages: conv.messages.map((m) =>
                    m.id === streamId ? { ...m, content: newFinalOutput } : m
                  ),
                  multiAgent: {
                    ...conv.multiAgent,
                    finalOutput: newFinalOutput,
                  },
                },
              },
            };
          } else {
            const agent = conv.multiAgent.agents[agentId];
            if (!agent) return state;

            return {
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...conv,
                  multiAgent: {
                    ...conv.multiAgent,
                    agents: {
                      ...conv.multiAgent.agents,
                      [agentId]: { ...agent, tokenBuffer: agent.tokenBuffer + delta },
                    },
                  },
                },
              },
            };
          }
        }),

      appendThinking: (conversationId, agentId, delta, text) =>
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv?.multiAgent) return state;

          const agent = conv.multiAgent.agents[agentId];
          if (!agent) return state;

          const newThinking = text ?? (agent.thinkingText + (delta ?? ""));

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                multiAgent: {
                  ...conv.multiAgent,
                  agents: {
                    ...conv.multiAgent.agents,
                    [agentId]: { ...agent, thinkingText: newThinking },
                  },
                },
              },
            },
          };
        }),

      addToolCalling: (conversationId, agentId, streamId, tool) =>
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv?.multiAgent) return state;

          const agent = conv.multiAgent.agents[agentId];
          if (!agent) return state;

          const newActiveTools = new Map(agent.activeTools);
          newActiveTools.set(tool.id, tool);

          const toolEvents = conv.toolEventsByMessage[streamId] ?? [];

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                toolEventsByMessage: {
                  ...conv.toolEventsByMessage,
                  [streamId]: [
                    ...toolEvents,
                    { phase: "calling", id: tool.id, name: tool.name, agent: agentId, args_preview: tool.argsPreview, server: tool.server, url: tool.url },
                  ],
                },
                multiAgent: {
                  ...conv.multiAgent,
                  agents: {
                    ...conv.multiAgent.agents,
                    [agentId]: { ...agent, activeTools: newActiveTools },
                  },
                },
              },
            },
          };
        }),

      completeToolCall: (conversationId, agentId, streamId, tool) =>
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv?.multiAgent) return state;

          const agent = conv.multiAgent.agents[agentId];
          if (!agent) return state;

          const newActiveTools = new Map(agent.activeTools);
          newActiveTools.delete(tool.id);

          const toolEvents = conv.toolEventsByMessage[streamId] ?? [];

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                toolEventsByMessage: {
                  ...conv.toolEventsByMessage,
                  [streamId]: [
                    ...toolEvents,
                    { phase: "completed", id: tool.id, name: tool.name, agent: agentId, ok: tool.ok, elapsed_ms: tool.elapsedMs, result_preview: tool.resultPreview, payload: tool.payload, server: tool.server, url: tool.url },
                  ],
                },
                multiAgent: {
                  ...conv.multiAgent,
                  agents: {
                    ...conv.multiAgent.agents,
                    [agentId]: {
                      ...agent,
                      activeTools: newActiveTools,
                      completedTools: [...agent.completedTools, tool],
                    },
                  },
                },
              },
            },
          };
        }),

      addCharts: (conversationId, charts) =>
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv?.multiAgent) return state;

          const newCharts = new Map(conv.multiAgent.charts);
          for (const chart of charts) {
            newCharts.set(chart.id, chart);
          }

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                multiAgent: {
                  ...conv.multiAgent,
                  charts: newCharts,
                },
              },
            },
          };
        }),

      setFinalMessage: (conversationId, text, streamId) =>
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv?.multiAgent) return state;

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                planningByMessage: { ...conv.planningByMessage, [streamId]: false },
                messages: conv.messages.map((m) =>
                  m.id === streamId ? { ...m, content: text || m.content } : m
                ),
                multiAgent: {
                  ...conv.multiAgent,
                  finalMessageText: text,
                },
              },
            },
          };
        }),

      setPlanningComplete: (conversationId, streamId) =>
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv) return state;

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                planningByMessage: { ...conv.planningByMessage, [streamId]: false },
              },
            },
          };
        }),

      // ─── Multi-Agent Snapshots ───
      setMultiAgentSnapshot: (conversationId, messageId, snapshot) =>
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv) return state;

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                multiAgentByMessageId: {
                  ...conv.multiAgentByMessageId,
                  [messageId]: snapshot,
                },
              },
            },
          };
        }),

      setMultiAgentSnapshots: (conversationId, snapshots) =>
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv) return state;

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                multiAgentByMessageId: {
                  ...conv.multiAgentByMessageId,
                  ...snapshots,
                },
              },
            },
          };
        }),

      // ─── Error Handling ───
      handleStreamError: (conversationId, streamId) => {
        get().controllers.delete(conversationId);

        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv) return state;

          const placeholder = conv.messages.find((m) => m.id === streamId);
          const hasContent = placeholder?.content?.trim();

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                isStreaming: false,
                planningByMessage: { ...conv.planningByMessage, [streamId]: false },
                messages: hasContent
                  ? conv.messages
                  : conv.messages.filter((m) => m.id !== streamId),
                multiAgent: conv.multiAgent
                  ? { ...conv.multiAgent, active: false }
                  : undefined,
              },
            },
          };
        });
      },
    }),
    { name: "assistant-store" }
  )
);

// ─────────────────────────────────────────────────────────────────────────────
// Selectors (for cleaner component access)
// ─────────────────────────────────────────────────────────────────────────────

// Stable empty defaults to prevent unnecessary re-renders
const EMPTY_MESSAGES: ChatMessage[] = [];
const EMPTY_MULTI_AGENT_BY_MESSAGE: Record<string, MultiAgentStreamState> = {};
const EMPTY_PLANNING: Record<string, boolean> = {};
const EMPTY_TOOL_EVENTS: Record<string, unknown[]> = {};

export const selectConversation = (conversationId: string | undefined) => (state: AssistantStore) =>
  conversationId ? state.conversations[conversationId] : undefined;

export const selectIsStreaming = (conversationId: string | undefined) => (state: AssistantStore) =>
  conversationId ? state.conversations[conversationId]?.isStreaming ?? false : false;

export const selectMessages = (conversationId: string | undefined) => (state: AssistantStore) =>
  conversationId ? state.conversations[conversationId]?.messages ?? EMPTY_MESSAGES : EMPTY_MESSAGES;

export const selectMultiAgent = (conversationId: string | undefined) => (state: AssistantStore) =>
  conversationId ? state.conversations[conversationId]?.multiAgent : undefined;

export const selectMultiAgentByMessageId = (conversationId: string | undefined) => (state: AssistantStore) =>
  conversationId ? state.conversations[conversationId]?.multiAgentByMessageId ?? EMPTY_MULTI_AGENT_BY_MESSAGE : EMPTY_MULTI_AGENT_BY_MESSAGE;

export const selectPlanningByMessage = (conversationId: string | undefined) => (state: AssistantStore) =>
  conversationId ? state.conversations[conversationId]?.planningByMessage ?? EMPTY_PLANNING : EMPTY_PLANNING;

export const selectToolEventsByMessage = (conversationId: string | undefined) => (state: AssistantStore) =>
  conversationId ? state.conversations[conversationId]?.toolEventsByMessage ?? EMPTY_TOOL_EVENTS : EMPTY_TOOL_EVENTS;

// Selector for streaming status by conversation ID (returns all streaming states)
export const selectStreamingById = (state: AssistantStore): Record<string, boolean> => {
  const result: Record<string, boolean> = {};
  for (const [id, conv] of Object.entries(state.conversations)) {
    if (conv.isStreaming) {
      result[id] = true;
    }
  }
  return result;
};

export default useAssistantStore;
